import React, { memo, useMemo, useCallback } from 'react';
import { Container, Card, CardBody } from 'reactstrap';
import { PageHeader } from '@modules/common';
import { useBloc } from '@core/utils/bloc';
import { ProjectChecklistResponse } from '@business/entities/project';
import { useRouteParam } from '@core/router';
import { SortableList } from '@modules/common/Sortable';
import { CheckListDetailPageBloc } from './CheckListDetailPageBloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { R } from '@core/utils/r';
import { SortEnd } from 'react-sortable-hoc';
import { SortableChecklistSectionItem } from './CheckListSectionItem';
import { openAddEditChecklistSection } from './AddEditChecklistSectionModal';
import { TopLoadingBar } from '@core/components/LoadingBar';

export const CheckListDetailPage = memo(() => {
  const checklistId = useRouteParam('id');
  const bloc = useBloc(CheckListDetailPageBloc, checklistId);
  const checklist = useObservable(bloc.template$) as ProjectChecklistResponse | null;
  const loading = useLoadingState(bloc.loading);

  const buttons = useMemo(() => {
    return [
      {
        title: 'Add Section',
        onClick: () => openAddEditChecklistSection({ checklistId }),
      },
    ];
  }, [checklistId]);

  const onDelete = useCallback(
    (id: number) => {
      bloc.deleteSection(checklistId, id).subscribe();
    },
    [bloc, checklistId],
  );

  const onStepDelete = useCallback(
    (id: number) => {
      bloc.deleteSectionStep(checklistId, id).subscribe();
    },
    [bloc, checklistId],
  );

  const onSectionSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const sectionIds = checklist!.sections.map(s => s.id);
      const newSectionIds = R.move(sectionIds, oldIndex, newIndex);
      bloc.sortSections(checklistId, newSectionIds).subscribe();
    },
    [bloc, checklist, checklistId],
  );

  const onStepSortEnd = useCallback(
    (sectionId: number, sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;

      const section = R.find(checklist!.sections, s => s.id === sectionId)!;

      const stepIds = section.steps.map(s => s.id);
      const newStepIds = R.move(stepIds, oldIndex, newIndex);
      bloc.sortSectionSteps(checklistId, sectionId, newStepIds).subscribe();
    },
    [checklist, bloc, checklistId],
  );

  if (loading.loading || !checklist) return <TopLoadingBar />;
  const sections = checklist.sections;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>{`Template Settings of ${checklist.name}`}</PageHeader>
        <Card>
          <CardBody>
            <SortableList lockAxis="y" distance={2} onSortEnd={onSectionSortEnd}>
              {sections.map((section, sectionIndex) => {
                const steps = section.steps;
                return (
                  <SortableChecklistSectionItem
                    section={section}
                    steps={steps}
                    index={sectionIndex}
                    key={sectionIndex}
                    onDelete={onDelete}
                    checklistId={checklistId}
                    onStepSortEnd={onStepSortEnd}
                    onStepDelete={onStepDelete}
                  />
                );
              })}
            </SortableList>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
});
